import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminCategories = () => {
  const { getAuthHeader } = useAuth();
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`, { headers: getAuthHeader() });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Erro ao carregar categorias');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (name === 'name' && !editingCategory) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        sort_order: parseInt(formData.sort_order) || 0
      };

      if (editingCategory) {
        await axios.put(`${API_URL}/categories/${editingCategory.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Categoria atualizada com sucesso!');
      } else {
        await axios.post(`${API_URL}/categories`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Categoria criada com sucesso!');
      }

      setShowModal(false);
      resetForm();
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      is_active: category.is_active,
      sort_order: category.sort_order.toString()
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta categoria?')) return;

    try {
      await axios.delete(`${API_URL}/categories/${categoryId}`, {
        headers: getAuthHeader()
      });
      toast.success('Categoria deletada com sucesso!');
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Erro ao deletar categoria');
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      is_active: true,
      sort_order: 0
    });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#273e82', fontFamily: 'Manrope' }}>
            Categorias
          </h1>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="primary-button flex items-center gap-2"
            data-testid="add-category-button"
          >
            <Plus size={20} />
            Nova Categoria
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full" data-testid="categories-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Nome</th>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Slug</th>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Ordem</th>
                <th className="text-left p-4 font-semibold" style={{ color: '#273e82' }}>Status</th>
                <th className="text-right p-4 font-semibold" style={{ color: '#273e82' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-4 font-semibold">{category.name}</td>
                  <td className="p-4 text-slate-600">{category.slug}</td>
                  <td className="p-4 text-slate-600">{category.sort_order}</td>
                  <td className="p-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                      category.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {category.is_active ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        data-testid={`edit-category-${category.id}`}
                      >
                        <Edit size={18} style={{ color: '#273e82' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 size={18} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div
            className="bg-white rounded-xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="category-modal"
          >
            <div className="border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{ color: '#273e82', fontFamily: 'Manrope' }}>
                {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                  Nome *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                  data-testid="category-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                  Slug *
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                  data-testid="category-slug-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                  Ordem de Exibição
                </label>
                <input
                  type="number"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  className="input-field"
                  data-testid="category-sort-input"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-5 h-5"
                  data-testid="category-active-checkbox"
                />
                <span className="font-semibold" style={{ color: '#273e82' }}>Categoria Ativa</span>
              </label>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 primary-button disabled:opacity-50"
                  data-testid="submit-category-button"
                >
                  {loading ? 'Salvando...' : editingCategory ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCategories;